import React, { useState, useEffect, useRef } from 'react';
import { createTenant, updateTenant, DEFAULT_TENANT_BRANDING, DEFAULT_TENANT_FEATURES } from '../firebase/tenants';
import './SuperAdmin.css';

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function TenantForm({ tenant, onSaved, onCancel }) {
  const logoInputRef = useRef(null);
  const isEdit = Boolean(tenant?.id);
  const [name, setName] = useState(tenant?.name || '');
  const [slug, setSlug] = useState(tenant?.slug || '');
  const [status, setStatus] = useState(tenant?.status || 'active');
  const [siteName, setSiteName] = useState(tenant?.branding?.siteName ?? DEFAULT_TENANT_BRANDING.siteName);
  const [logoUrl, setLogoUrl] = useState(tenant?.branding?.logoUrl ?? DEFAULT_TENANT_BRANDING.logoUrl);
  const [primaryColor, setPrimaryColor] = useState(tenant?.branding?.primaryColor ?? DEFAULT_TENANT_BRANDING.primaryColor);
  const [accentColor, setAccentColor] = useState(tenant?.branding?.accentColor ?? DEFAULT_TENANT_BRANDING.accentColor);
  const [contactFormEnabled, setContactFormEnabled] = useState(tenant?.features?.contactFormEnabled ?? DEFAULT_TENANT_FEATURES.contactFormEnabled);
  const [showClasses, setShowClasses] = useState(tenant?.features?.showClasses ?? DEFAULT_TENANT_FEATURES.showClasses);
  const [showTrainers, setShowTrainers] = useState(tenant?.features?.showTrainers ?? DEFAULT_TENANT_FEATURES.showTrainers);
  const [customDomainsStr, setCustomDomainsStr] = useState((Array.isArray(tenant?.customDomains) ? tenant.customDomains.join(', ') : '') || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tenant) {
      setName(tenant.name || '');
      setSlug(tenant.slug || '');
      setStatus(tenant.status || 'active');
      setSiteName(tenant.branding?.siteName ?? DEFAULT_TENANT_BRANDING.siteName);
      setLogoUrl(tenant.branding?.logoUrl ?? DEFAULT_TENANT_BRANDING.logoUrl);
      setPrimaryColor(tenant.branding?.primaryColor ?? DEFAULT_TENANT_BRANDING.primaryColor);
      setAccentColor(tenant.branding?.accentColor ?? DEFAULT_TENANT_BRANDING.accentColor);
      setContactFormEnabled(tenant.features?.contactFormEnabled ?? true);
      setShowClasses(tenant.features?.showClasses ?? true);
      setShowTrainers(tenant.features?.showTrainers ?? true);
      setCustomDomainsStr(Array.isArray(tenant.customDomains) ? tenant.customDomains.join(', ') : '');
    }
  }, [tenant]);

  const deriveSlug = (value) => value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const dataUrl = await readFileAsBase64(file);
      setLogoUrl(dataUrl);
    } catch (_) {
      setError('Failed to read image.');
    }
    e.target.value = '';
  };

  const handleNameChange = (e) => {
    const v = e.target.value;
    setName(v);
    if (!isEdit) setSlug(deriveSlug(v));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const branding = { siteName, logoUrl, primaryColor, accentColor };
    const features = { contactFormEnabled, showClasses, showTrainers };
    const customDomains = customDomainsStr.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (isEdit) {
      const result = await updateTenant(tenant.id, { name, slug, status, branding, features, customDomains });
      if (result.success) {
        onSaved();
      } else {
        setError(result.message);
      }
    } else {
      const result = await createTenant({ name, slug, status, branding, features, customDomains });
      if (result.success) {
        onSaved();
      } else {
        setError(result.message);
      }
    }
    setSaving(false);
  };

  return (
    <div className="tenant-form-container">
      <h2>{isEdit ? 'Edit Tenant' : 'Create Tenant'}</h2>
      <form onSubmit={handleSubmit} className="tenant-form">
        <section className="tenant-form-section">
          <h3>Basic</h3>
          <label>Name</label>
          <input type="text" value={name} onChange={handleNameChange} required />
          <label>Slug (URL path)</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g. demusclefactory"
          />
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          <label>Custom domains (optional)</label>
          <input
            type="text"
            value={customDomainsStr}
            onChange={(e) => setCustomDomainsStr(e.target.value)}
            placeholder="e.g. www.demusclefactory.com, demusclefactory.com"
          />
          <span className="tenant-form-hint">Comma-separated. When users visit these domains, this tenant&apos;s site is shown at / and /contact. Add domains in Firebase Hosting and point DNS to your project first.</span>
        </section>
        <section className="tenant-form-section">
          <h3>Public site (branding)</h3>
          <label>Site name</label>
          <input type="text" value={siteName} onChange={(e) => setSiteName(e.target.value)} />
          <label>Logo (URL or upload)</label>
          {(!logoUrl || !logoUrl.startsWith('data:')) && (
            <input type="text" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://... or upload below" />
          )}
          {logoUrl?.startsWith('data:') && (
            <div className="tenant-form-logo-uploaded">
              <span>Uploaded image (base64)</span>
              <button type="button" className="super-admin-btn small" onClick={() => setLogoUrl('')}>Clear</button>
            </div>
          )}
          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
          <button type="button" className="super-admin-btn small" style={{ marginTop: '0.5rem' }} onClick={() => logoInputRef.current?.click()}>
            {logoUrl?.startsWith('data:') ? 'Replace logo' : 'Upload logo (saved as base64)'}
          </button>
          {logoUrl && (logoUrl.startsWith('data:') || logoUrl.startsWith('http')) && (
            <div style={{ marginTop: '0.5rem' }}>
              <img src={logoUrl} alt="Logo preview" style={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain' }} />
            </div>
          )}
          <label>Primary color</label>
          <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
          <label>Accent color</label>
          <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} />
        </section>
        <section className="tenant-form-section">
          <h3>Features</h3>
          <label className="tenant-form-check">
            <input
              type="checkbox"
              checked={contactFormEnabled}
              onChange={(e) => setContactFormEnabled(e.target.checked)}
            />
            Contact form enabled
          </label>
          <label className="tenant-form-check">
            <input
              type="checkbox"
              checked={showClasses}
              onChange={(e) => setShowClasses(e.target.checked)}
            />
            Show Classes section
          </label>
          <label className="tenant-form-check">
            <input
              type="checkbox"
              checked={showTrainers}
              onChange={(e) => setShowTrainers(e.target.checked)}
            />
            Show Trainers section
          </label>
        </section>
        {error && <p className="tenant-form-error">{error}</p>}
        <div className="tenant-form-actions">
          <button type="button" className="super-admin-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="super-admin-btn primary" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TenantForm;
